import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import {
    Area,
    AreaChart,
    ResponsiveContainer,
    Tooltip as RTooltip,
    XAxis,
} from "recharts";
import {
    Plus,
    Sparkles,
    ArrowUpRight,
    Trophy,
    Clock,
    Bot,
    Zap,
    MessageSquare,
    LineChart as LineChartIcon,
    Layers,
    Radar,
    FileSignature,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { listTenders, getStats } from "@/lib/api";
import { fmtEUR, daysUntil } from "@/lib/format";
import { DeadlineAlerts } from "@/components/DeadlineAlerts";
import { SpainHeatMap } from "@/components/SpainHeatMap";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { dashboardTour } from "@/tours";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { useAuth } from "@/context/AuthContext";

// ---------- Helpers ----------
const greeting = () => {
    const h = new Date().getHours();
    if (h < 6) return "Buenas noches";
    if (h < 12) return "Buenos días";
    if (h < 20) return "Buenas tardes";
    return "Buenas noches";
};

function budgetByMonth(tenders) {
    const buckets = new Map();
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        buckets.set(k, {
            month: d.toLocaleString("es-ES", { month: "short" }),
            total: 0,
            count: 0,
        });
    }
    for (const t of tenders) {
        if (!t.created_at) continue;
        const d = new Date(t.created_at);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const slot = buckets.get(k);
        if (slot) {
            slot.total += t.budget || 0;
            slot.count += 1;
        }
    }
    return Array.from(buckets.values());
}

function AnimatedNumber({ value, decimals = 0, format }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });
    const mv = useMotionValue(0);
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const controls = animate(mv, value, {
            duration: 1.2,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setDisplay(v),
        });
        return () => controls.stop();
    }, [inView, value, mv]);
    const text = format ? format(display) : display.toFixed(decimals);
    return <span ref={ref}>{text}</span>;
}

const SparkTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const v = payload[0].payload;
    return (
        <div className="bg-ink-950 text-white text-xs rounded-lg px-3 py-2 shadow-pop-lg">
            <div className="font-bold uppercase tracking-wider text-[10px] text-white/60">
                {v.month}
            </div>
            <div className="font-mono font-bold">{fmtEUR(v.total)}</div>
            <div className="text-[10px] text-white/70">{v.count} licitación(es)</div>
        </div>
    );
};

// ---------- Component ----------
export default function Dashboard() {
    const { user } = useAuth();
    const [tenders, setTenders] = useState([]);
    const [stats, setStats] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([listTenders(), getStats()])
            .then(([t, s]) => {
                setTenders(t);
                setStats(s);
            })
            .catch(() => {});
    }, []);

    const pipeline = useMemo(() => {
        const by = stats?.by_status || {};
        const draft = by.draft || 0;
        const analyzed = by.analyzed || 0;
        const inProg = by.in_progress || 0;
        const submitted = by.submitted || 0;
        const total = draft + analyzed + inProg + submitted || 1;
        return [
            { key: "draft", label: "Borrador", count: draft, pct: (draft / total) * 100, color: "bg-slate-300" },
            { key: "analyzed", label: "Analizadas", count: analyzed, pct: (analyzed / total) * 100, color: "bg-violet-500" },
            { key: "in_progress", label: "Preparando", count: inProg, pct: (inProg / total) * 100, color: "bg-amber-500" },
            { key: "submitted", label: "Presentadas", count: submitted, pct: (submitted / total) * 100, color: "bg-emerald-500" },
        ];
    }, [stats]);

    const won = stats?.by_outcome?.won || 0;
    const lostOrPending =
        (stats?.by_outcome?.lost || 0) + (stats?.by_outcome?.pending || 0);
    const winRate =
        won + lostOrPending > 0
            ? Math.round((won / (won + lostOrPending)) * 100)
            : null;

    const annexesCount = useMemo(
        () => tenders.reduce((acc, t) => acc + (t.annexes?.length || 0), 0),
        [tenders],
    );
    const analyzedCount = stats?.by_status?.analyzed || 0;
    const hoursSaved = analyzedCount * 8 + annexesCount * 2;

    const monthlyData = useMemo(() => budgetByMonth(tenders), [tenders]);

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.06, delayChildren: 0.04 },
        },
    };
    const itemVariants = {
        hidden: { opacity: 0, y: 12 },
        show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
    };

    const firstName = (user?.name || user?.email?.split("@")[0] || "").split(" ")[0];

    const tour = useTour("tour:dashboard", dashboardTour, { autoStart: true });

    return (
        <Layout>
            <motion.div
                className="w-full max-w-[1600px] mx-auto p-4 md:p-8"
                data-testid="dashboard-page"
                initial="hidden"
                animate="show"
                variants={containerVariants}
            >
                {/* Header */}
                <motion.div
                    className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8"
                    variants={itemVariants}
                >
                    <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600 mb-2 flex items-center gap-2">
                            <span className="inline-block w-6 h-px bg-brand-600" />
                            Centro de operaciones
                        </div>
                        <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight text-ink-950 leading-[0.95]">
                            {greeting()}
                            {firstName ? (
                                <>
                                    , <span className="text-brand-600">{firstName}</span>
                                </>
                            ) : null}
                            .
                        </h1>
                        <p className="text-base md:text-lg text-ink-500 mt-3 font-medium max-w-xl leading-snug">
                            Tu pipeline de licitaciones en directo —{" "}
                            <span className="text-ink-900 font-bold">
                                {(stats?.total || 0).toLocaleString("es-ES")} activas
                            </span>{" "}
                            con la IA trabajando en segundo plano.
                        </p>
                    </div>
                    <Button
                        className="rounded-full bg-ink-950 text-white hover:bg-ink-800 h-12 px-7 font-bold shadow-pop-lg transition-all duration-300 hover:shadow-[0_10px_40px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 group"
                        onClick={() => navigate("/app/nueva")}
                        data-testid="dashboard-new-tender-btn"
                    >
                        <Plus size={16} className="mr-2 transition-transform group-hover:rotate-90" />
                        Nueva licitación
                    </Button>
                </motion.div>

                {/* Onboarding for new customers — auto-hides for super-admin or completed */}
                <OnboardingChecklist user={user} tenders={tenders} />

                {/* KPI ROW */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6" data-testid="dashboard-kpis">
                    {/* Presupuesto + sparkline */}
                    <motion.div
                        variants={itemVariants}
                        className="md:col-span-2 relative bg-white border border-ink-100 rounded-2xl p-6 md:p-8 overflow-hidden group"
                    >
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-brand-100/60 to-cyan-100/40 rounded-full blur-3xl pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity" />
                        <div className="relative flex items-start justify-between">
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500 mb-2">
                                    Presupuesto movilizado
                                </div>
                                <div className="font-display font-black text-ink-950 tracking-tight text-5xl md:text-6xl leading-none">
                                    <AnimatedNumber
                                        value={stats?.total_budget || 0}
                                        format={(v) => fmtEUR(v)}
                                    />
                                </div>
                                <div className="text-sm text-ink-500 mt-3 font-medium flex items-center gap-2">
                                    <ArrowUpRight size={14} className="text-emerald-600" />
                                    <span>
                                        {(stats?.total || 0).toLocaleString("es-ES")} licitaciones en cartera
                                    </span>
                                </div>
                            </div>
                            <div className="hidden md:flex w-10 h-10 rounded-xl bg-ink-950 text-white items-center justify-center shadow-pop shrink-0">
                                <LineChartIcon size={17} />
                            </div>
                        </div>
                        <div className="relative h-24 md:h-28 mt-6 -mx-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={monthlyData}>
                                    <defs>
                                        <linearGradient id="grad-budget" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#0055FF" stopOpacity={0.35} />
                                            <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <XAxis
                                        dataKey="month"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 600 }}
                                    />
                                    <RTooltip
                                        content={<SparkTooltip />}
                                        cursor={{ stroke: "#0055FF", strokeWidth: 1, strokeDasharray: "3 3" }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="total"
                                        stroke="#0055FF"
                                        strokeWidth={2.5}
                                        fill="url(#grad-budget)"
                                        dot={{ fill: "#0055FF", r: 3, strokeWidth: 2, stroke: "white" }}
                                        activeDot={{ r: 5, strokeWidth: 3 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </motion.div>

                    {/* Pipeline */}
                    <motion.div
                        variants={itemVariants}
                        className="bg-white border border-ink-100 rounded-2xl p-6 flex flex-col cursor-pointer hover:shadow-pop transition-shadow"
                        onClick={() => navigate("/app/licitaciones")}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">
                                Pipeline
                            </div>
                            <Sparkles size={14} className="text-brand-600" />
                        </div>
                        <div className="font-display font-black text-ink-950 tracking-tight text-4xl md:text-5xl leading-none">
                            <AnimatedNumber value={stats?.total || 0} />
                        </div>
                        <div className="text-xs text-ink-500 font-medium mt-1.5 mb-4">
                            Total licitaciones
                        </div>
                        <div className="mt-auto">
                            <div
                                className="flex h-2 rounded-full overflow-hidden bg-ink-50 mb-3"
                                data-testid="pipeline-bar"
                            >
                                {pipeline.map((p) => (
                                    <motion.div
                                        key={p.key}
                                        className={`${p.color} h-full`}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${p.pct}%` }}
                                        transition={{ duration: 0.9, ease: "easeOut", delay: 0.3 }}
                                        title={`${p.label}: ${p.count}`}
                                    />
                                ))}
                            </div>
                            <div className="space-y-1.5">
                                {pipeline.map((p) => (
                                    <div
                                        key={p.key}
                                        className="flex items-center justify-between text-xs"
                                        data-testid={`pipeline-${p.key}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2 h-2 rounded-sm ${p.color}`} />
                                            <span className="text-ink-600 font-medium">{p.label}</span>
                                        </div>
                                        <span className="font-mono font-bold text-ink-900 tabular-nums">
                                            {p.count}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* AI Value */}
                    <motion.div
                        variants={itemVariants}
                        className="relative overflow-hidden rounded-2xl border border-violet-300 bg-ink-950 text-white p-6 flex flex-col"
                    >
                        <div
                            className="absolute inset-0 opacity-30"
                            style={{
                                backgroundImage:
                                    "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
                                backgroundSize: "24px 24px",
                            }}
                        />
                        <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br from-violet-500 to-cyan-400 rounded-full blur-3xl opacity-40" />
                        <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-gradient-to-tr from-brand-500 to-violet-400 rounded-full blur-3xl opacity-30" />
                        <div className="relative">
                            <div className="flex items-center justify-between mb-4">
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300">
                                    IA Claude 4.5
                                </div>
                                <div className="w-7 h-7 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                                    <Bot size={14} className="text-white" />
                                </div>
                            </div>
                            <div className="font-display font-black tracking-tight text-5xl leading-none mb-1">
                                <AnimatedNumber value={hoursSaved} />
                                <span className="text-2xl text-white/60 ml-1.5 font-bold">h</span>
                            </div>
                            <div className="text-xs text-white/70 font-medium mt-1">
                                ahorradas este mes
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/10">
                                <div>
                                    <div className="text-xl font-display font-black text-white">
                                        <AnimatedNumber value={analyzedCount} />
                                    </div>
                                    <div className="text-[10px] text-white/60 font-bold uppercase tracking-wider">
                                        Análisis IA
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xl font-display font-black text-white">
                                        <AnimatedNumber value={annexesCount} />
                                    </div>
                                    <div className="text-[10px] text-white/60 font-bold uppercase tracking-wider">
                                        Anexos generados
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Mini KPIs */}
                <motion.div
                    variants={itemVariants}
                    className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8"
                >
                    <MiniKpi
                        label="Win rate"
                        value={winRate != null ? `${winRate}%` : "—"}
                        Icon={Trophy}
                        accent="emerald"
                        sub={
                            won + lostOrPending > 0
                                ? `${won} ganada(s) / ${won + lostOrPending} resueltas`
                                : "Sin resoluciones aún"
                        }
                        onClick={() => navigate("/app/analiticas")}
                    />
                    <MiniKpi
                        label="Plazos < 7 días"
                        value={
                            tenders.filter((t) => {
                                const d = daysUntil(t.deadline);
                                return d !== null && d >= 0 && d < 7;
                            }).length
                        }
                        Icon={Clock}
                        accent="red"
                        sub="Atención inmediata"
                    />
                    <MiniKpi
                        label="Anexos firmados"
                        value={tenders.reduce(
                            (acc, t) =>
                                acc + (t.annexes || []).filter((a) => a.signed_storage_path).length,
                            0,
                        )}
                        Icon={Zap}
                        accent="violet"
                        sub="PAdES eIDAS"
                    />
                    <MiniKpi
                        label="Chats con IA"
                        value={tenders.reduce(
                            (acc, t) => acc + (t.chat_messages?.length || 0),
                            0,
                        )}
                        Icon={MessageSquare}
                        accent="brand"
                        sub="Consultas al pliego"
                    />
                </motion.div>

                {/* Deadline alerts */}
                <motion.div variants={itemVariants}>
                    <DeadlineAlerts />
                </motion.div>

                {/* Spain heat map */}
                <motion.div variants={itemVariants} className="mb-8">
                    <SpainHeatMap tenders={tenders} />
                </motion.div>

                {/* Quick actions row */}
                <motion.div
                    variants={itemVariants}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-4"
                >
                    <QuickAction
                        Icon={Layers}
                        title="Cartera completa"
                        desc="Buscar, filtrar y gestionar todas tus licitaciones"
                        to="/app/licitaciones"
                        accent="brand"
                        testid="quick-licitaciones"
                    />
                    <QuickAction
                        Icon={Radar}
                        title="Buscador PLCSP"
                        desc="855k licitaciones de todos los ámbitos de España"
                        to="/app/buscar-plcsp"
                        accent="cyan"
                        testid="quick-plcsp"
                    />
                    <QuickAction
                        Icon={FileSignature}
                        title="Cumplimentar anexo"
                        desc="Sube un PDF en blanco y la IA lo rellena por ti"
                        to="/app/cumplimentar-anexos"
                        accent="violet"
                        testid="quick-anexos"
                    />
                </motion.div>
            </motion.div>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}

// ---------- Sub-components ----------
const ACCENTS = {
    emerald: { iconBg: "bg-emerald-600", text: "text-emerald-700", border: "border-emerald-200" },
    red: { iconBg: "bg-red-600", text: "text-red-700", border: "border-red-200" },
    violet: { iconBg: "bg-violet-600", text: "text-violet-700", border: "border-violet-200" },
    brand: { iconBg: "bg-brand-600", text: "text-brand-700", border: "border-brand-200" },
    cyan: { iconBg: "bg-cyan-600", text: "text-cyan-700", border: "border-cyan-200" },
};

function MiniKpi({ label, value, Icon, accent = "brand", sub, onClick }) {
    const a = ACCENTS[accent] || ACCENTS.brand;
    const interactive = !!onClick;
    return (
        <motion.div
            whileHover={interactive ? { y: -2 } : undefined}
            onClick={onClick}
            className={`relative bg-white border ${a.border} rounded-2xl p-5 ${
                interactive ? "cursor-pointer hover:shadow-pop transition-shadow" : ""
            }`}
            data-testid={`minikpi-${label}`}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400">
                    {label}
                </div>
                <div
                    className={`w-7 h-7 rounded-lg ${a.iconBg} text-white flex items-center justify-center shadow-pop`}
                >
                    <Icon size={13} />
                </div>
            </div>
            <div className="font-display font-black text-ink-950 tracking-tight text-3xl leading-none">
                {typeof value === "number" ? <AnimatedNumber value={value} /> : value}
            </div>
            {sub && <div className={`text-[11px] mt-1.5 font-medium ${a.text}`}>{sub}</div>}
        </motion.div>
    );
}

function QuickAction({ Icon, title, desc, to, accent = "brand", testid }) {
    const navigate = useNavigate();
    const a = ACCENTS[accent] || ACCENTS.brand;
    return (
        <motion.button
            type="button"
            whileHover={{ y: -2 }}
            onClick={() => navigate(to)}
            className={`text-left bg-white border ${a.border} rounded-2xl p-5 hover:shadow-pop transition-shadow group`}
            data-testid={testid}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`w-10 h-10 rounded-xl ${a.iconBg} text-white flex items-center justify-center shadow-pop shrink-0`}
                >
                    <Icon size={17} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-ink-950 text-base leading-tight mb-0.5">
                        {title}
                    </div>
                    <div className="text-xs text-ink-500 font-medium leading-snug">{desc}</div>
                </div>
                <ArrowUpRight
                    size={15}
                    className={`${a.text} opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0`}
                />
            </div>
        </motion.button>
    );
}
